import FreehandMode from '@/lib/freehand-mode-manual';
import RectangleMode from '@/lib/rectangle-draw-mode';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { useEffect, useRef, memo } from 'react';
import { ControlPosition, useControl, useMap, IControl } from 'react-map-gl';
import { MAPBOX_DRAW_STYLES } from '@/constants/polygonStyles';

const pluginEvents: Record<string, string> = {
  Clear: 'clear',
  Loading: 'loading',
  Create: 'draw.create',
  Delete: 'draw.delete',
  Update: 'draw.update',
  Add: 'draw.add',
  ModeChange: 'draw.modechange',
  SelectionChange: 'draw.selectionchange',
};

export type DrawControlProps = {
  position: ControlPosition;
  onAdd?: () => void;
  onControlChange: (control: MapboxDraw) => void;
  onDrawCreate?: (e: any) => void;
  onDrawUpdate?: (e: any) => void;
  onDrawDelete?: (e: any) => void;
  onDrawModeChange?: (e: any) => void;
  onDrawSelectionChange?: (e: any) => void;
  /** Overrides the `draw_polygon` mode implementation (default: FreehandMode, which supports
   * both click-by-click and press-drag-release drawing). Pass Mapbox's own built-in
   * `MapboxDraw.modes.draw_polygon` for click-by-click-only behavior with no freehand drag. */
  polygonMode?: any;
  [key: string]: any;
};

export default memo<DrawControlProps>(function DrawPolygonControl(props) {
  const control = useControl(
    () => {
      const customModes: { [modeKey: string]: any } = {
        ...MapboxDraw.modes,
        draw_polygon: props.polygonMode || FreehandMode,
        draw_rectangle: RectangleMode,
      };

      // Create MapboxDraw instance and cast it to IControl
      // Uses unified polygon styles from @/constants/polygonStyles
      return new MapboxDraw({
        modes: customModes,
        displayControlsDefault: false,
        styles: MAPBOX_DRAW_STYLES,
      }) as unknown as IControl;
    },
    () => {
      if (props.onAdd) {
        props.onAdd();
      }
    },
    () => {
    },
    {
      position: props.position,
    }
  );

  const { current: map } = useMap();

  const onControlChangeRef = useRef(props.onControlChange);
  onControlChangeRef.current = props.onControlChange;

  useEffect(() => {
    if (control && onControlChangeRef.current) {
      onControlChangeRef.current(control as unknown as MapboxDraw);
    }
  }, [control]);

  const propsRef = useRef(props);
  propsRef.current = props;

  useEffect(() => {
    if (!map) return;
    const handlers: Record<string, (e: any) => void> = {};

    Object.entries(pluginEvents).forEach(([key, eventName]) => {
      const propKey = `onDraw${key}`;
      handlers[eventName] = (e: any) => {
        const fn = propsRef.current[propKey];
        if (typeof fn === 'function') {
          fn(e);
        }
      };
      map.on(eventName, handlers[eventName]);
    });

    return () => {
      Object.entries(handlers).forEach(([eventName, handler]) => {
        map.off(eventName, handler);
      });
    };
  }, [map]);

  return null;
});
